/*
 * Product card quick add — the cart button on a product card
 * (snippets/product-card.liquid, rendered where a section passes quick_add),
 * in the two of its three modes that need a script ("Go to product page" is a
 * plain link):
 *
 * Add to cart directly ([data-quick-add]): only a card that can name one
 * variant gets a button — a single-variant product, or a colour-only product
 * whose chosen swatch names it (the swatch script keeps data-variant-id
 * current); anything else with options links to the product page instead, so
 * this never has to guess a size.
 *
 * Quick add window ([data-quick-add-open]): a link to the product page that
 * opens the window instead (Figma R9wzAiaE155GWujEn4gI9U, 9061:28959). Its
 * contents are sections/quick-add.liquid, rendered for that product by the
 * Section Rendering API on the link's own address — so a colour chosen on the
 * card (the swatch script keeps the href current) is the one the window opens
 * on — and kept per address for the visit. The window is the theme's shared
 * overlay (overlay-panel.js: focus trap, Escape, scrim, scroll lock, focus
 * back to the card's button), one for the page, built on first use. Choosing
 * options works as the product page's picker does: from the variant data the
 * section embeds, each value is struck through when it has nothing in stock
 * given the other choices, and the price, the button and View details follow
 * the variant. If the window's contents can't be had, the link goes to the
 * product page as it would without a script.
 *
 * Speed: the contents are requested at the first sign of interest - the
 * pointer reaching the button, the start of a tap, keyboard focus - so the
 * click rarely waits. The window's stylesheet is a print stylesheet until
 * then, so it never delays the page it is on. And the card's photo, already
 * loaded, stands in for the window's sharper copy of it until that arrives.
 *
 * Add to cart in the window goes the same way as the card's button, below;
 * the drawer opening closes the window (one overlay at a time), and focus
 * goes back to the card's button when the drawer closes, not to a window that
 * is gone. A refusal the drawer doesn't show is shown in the window, under
 * its button, as an alert.
 *
 * The add is handed to the cart drawer through a `cart:add` event, so the
 * drawer's own request/render/count logic does the work in one round trip and
 * the drawer opens on success (its focus handling returns to this button when
 * it closes). With no drawer on the page, it adds directly and goes to the
 * cart page instead.
 *
 * A refusal ("All 2 … are in your cart") has to reach everyone, not just
 * a screen reader (WCAG 3.3.1), and a card has no room for it: with a drawer,
 * the drawer opens and shows Shopify's reason at its top (`showInCart`), as
 * an alert. Only with no drawer, or no reason given, is it announced from here.
 *
 * One delegated listener serves every card, including cards added later. ES
 * modules run once per URL, but the flag keeps a second copy from double-adding.
 */

import { OverlayPanel } from 'overlay-panel';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const announce = (() => {
  let region = null;
  return (message) => {
    if (!region) {
      region = document.createElement('p');
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    // Clear first so repeating the same message is still announced.
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  };
})();

const setBusy = (button, busy) => {
  if (busy) {
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
  } else {
    button.removeAttribute('aria-disabled');
    button.removeAttribute('aria-busy');
  }
};

const failure = (button) => {
  const title = [button.dataset.productTitle, button.dataset.variantTitle].filter(Boolean).join(', ') || 'That item';
  announce(`${title} couldn't be added to your cart. Please try again.`);
};

/* No drawer to hand off to: add directly, then show the cart page. */
const addWithoutDrawer = async (button) => {
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }),
    });
    if (response.ok) {
      window.location.href = `${root}cart`;
      return;
    }
    failure(button);
  } catch (error) {
    failure(button);
  }
  setBusy(button, false);
};

/* ---- Quick add window ---- */

// A rolling label (snippets/roll-text.liquid) holds its text twice, for the
// hover; both copies change together.
const setRollText = (el, text) => {
  const lines = el.querySelectorAll('.roll__line');
  if (lines.length) lines.forEach((line) => { line.textContent = text; });
  else el.textContent = text;
};

/* The window's shell: one for the page, made the first time it's needed. The
   close button is Figma's 46px square in the top right corner. It sits beside
   the scrolling contents, not in them, so on a short screen it stays in view
   while they scroll. */
let shell = null;
const getShell = () => {
  if (shell) return shell;
  const layer = document.createElement('div');
  layer.className = 'overlay quick-add';
  layer.dataset.state = 'closed';
  layer.hidden = true;
  layer.innerHTML = `
    <div class="overlay__scrim" aria-hidden="true"></div>
    <div class="overlay__panel quick-add__panel" role="dialog" aria-modal="true">
      <button type="button" class="overlay__contents quick-add__close" data-quick-add-close aria-label="Close quick add">
        <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M0.6 0.6L11.4 11.4M11.4 0.6L0.6 11.4" /></svg>
      </button>
      <div class="overlay__contents quick-add__contents" data-quick-add-slot></div>
    </div>`;
  document.body.appendChild(layer);
  const dialog = layer.querySelector('.quick-add__panel');
  const panel = new OverlayPanel({ root: layer, dialog });
  layer.querySelector('[data-quick-add-close]').addEventListener('click', () => panel.close());
  shell = { dialog, panel, slot: layer.querySelector('[data-quick-add-slot]') };
  return shell;
};

/* The window's styles (assets/quick-add.css) come as a print stylesheet, so
   they never hold up the page they're on: the browser fetches them quietly,
   and they're switched on here, at the first sign the window is wanted.
   Resolves once they apply (or have failed, or after a few seconds); one
   promise per stylesheet, however often it's asked. */
const styleSheets = new WeakMap();
const stylesReady = () =>
  Promise.all(
    [...document.querySelectorAll('link[data-quick-add-styles]')].map((link) => {
      if (!styleSheets.has(link)) {
        if (link.media !== 'all') link.media = 'all';
        styleSheets.set(
          link,
          link.sheet
            ? Promise.resolve()
            : new Promise((resolve) => {
                link.addEventListener('load', resolve, { once: true });
                link.addEventListener('error', resolve, { once: true });
                window.setTimeout(resolve, 4000);
              })
        );
      }
      return styleSheets.get(link);
    })
  );

/* The window's contents for a product address, rendered by sections/quick-
   add.liquid and parsed once; each opening gets its own copy. Asked for at
   the first sign of interest (the pointer reaching the button, or focus), so
   by the click it's usually here. One request per address for the visit; a
   failed one is forgotten, so the next try asks again. */
const pages = new Map();
const request = (href) => {
  const url = new URL(href, window.location.href);
  url.searchParams.set('section_id', 'quick-add');
  const key = url.toString();
  if (!pages.has(key)) {
    const page = fetch(key, { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Quick add: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const body = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-add-content]');
        if (!body) throw new Error('Quick add: nothing to show');
        return body;
      });
    page.catch(() => pages.delete(key));
    pages.set(key, page);
  }
  return pages.get(key);
};

const prefetch = (event) => {
  const opener = event.target.closest && event.target.closest('[data-quick-add-open]');
  if (!opener) return;
  request(opener.href).catch(() => {});
  stylesReady();
};

/* The card's photo is already on screen: while the window's sharper copy of
   the same picture loads, it stands in, so the window never opens on an empty
   frame. Only when it is the same file (the window may show the variant's own
   image); settlePhoto takes it away again. */
const borrowCardPhoto = (body, opener) => {
  const media = body.querySelector('[data-quick-add-media]');
  const image = media && media.querySelector('img');
  const card = opener.closest('.product-card');
  const shown = card && card.querySelector('img.product-card__image');
  if (!image || !shown || !shown.complete || !shown.currentSrc) return;
  const file = (src) => {
    try {
      return new URL(src, window.location.href).pathname;
    } catch (error) {
      return '';
    }
  };
  if (file(shown.currentSrc) !== file(image.getAttribute('src'))) return;
  media.style.backgroundImage = `url("${shown.currentSrc}")`;
};

/* Once the window is laid out. The photo is a square crop (sections/quick-
   add.liquid) and its box is square, or wider than tall on a phone - unless
   the details outgrow the design's one row of options (a second option, a
   long title) and stretch it taller: then the square is drawn at the box's
   height, and the browser is told so, in the same task as the window opened,
   before it has chosen a file - one request, the right size, not a small one
   enlarged. The stand-in goes once the photo the browser settled on has
   decoded: a cut-out PNG mustn't show it through its transparent parts. */
const settlePhoto = (body) => {
  const media = body.querySelector('[data-quick-add-media]');
  const image = media && media.querySelector('img');
  if (!image) return;
  const box = media.getBoundingClientRect();
  if (image.srcset && box.height > box.width * 1.05) image.sizes = `${Math.ceil(box.height)}px`;
  if (!media.style.backgroundImage) return;
  const clear = () => {
    media.style.backgroundImage = '';
  };
  image.decode().then(clear, () => image.addEventListener('load', clear, { once: true }));
};

/* Does the variant fit every chosen value? A null value matches anything. */
const matches = (variant, chosen) => variant.options.every((value, i) => chosen[i] === null || value === chosen[i]);

/* The picker, the price and the button of one rendering of the window. */
const setUpWindow = (body, opener) => {
  let variants = [];
  try {
    variants = JSON.parse(body.querySelector('[data-quick-add-variants]').textContent);
  } catch (error) {
    variants = [];
  }
  const form = body.querySelector('[data-quick-add-form]');
  const idInput = body.querySelector('[data-quick-add-variant]');
  const inputs = [...body.querySelectorAll('[data-quick-add-option]')];
  const price = body.querySelector('[data-quick-add-price]');
  const saleNote = body.querySelector('[data-quick-add-sale-note]');
  const compare = body.querySelector('[data-quick-add-compare]');
  const compareText = body.querySelector('[data-quick-add-compare-text]');
  const submit = body.querySelector('[data-quick-add-submit]');
  const submitText = body.querySelector('[data-quick-add-submit-text]');
  const details = body.querySelector('[data-quick-add-details]');
  const error = body.querySelector('[data-quick-add-error]');
  const status = body.querySelector('[data-quick-add-status]');
  const optionCount = variants.length ? variants[0].options.length : 0;

  const say = (message) => {
    // Clear first so repeating the same message is still announced.
    status.textContent = '';
    window.setTimeout(() => {
      status.textContent = message;
    }, 50);
  };
  const showError = (message) => {
    error.textContent = message;
    error.hidden = !message;
  };

  const chosenOptions = () => {
    const chosen = [];
    for (let i = 0; i < optionCount; i += 1) {
      const input = body.querySelector(`[data-quick-add-option="${i}"]:checked`);
      chosen.push(input ? input.value : null);
    }
    return chosen;
  };

  const currentVariant = (chosen) => {
    // No options (a single-variant product): the hidden field already says which.
    if (!inputs.length) return variants.find((variant) => String(variant.id) === idInput.value) || variants[0] || null;
    if (chosen.some((value) => value === null)) return null;
    return variants.find((variant) => matches(variant, chosen)) || null;
  };

  const refresh = (userChange) => {
    const chosen = chosenOptions();
    const variant = currentVariant(chosen);

    // Each value's stock, holding the other options at what's chosen.
    inputs.forEach((input) => {
      const probe = [...chosen];
      probe[Number(input.dataset.quickAddOption)] = input.value;
      const inStock = variants.some((candidate) => candidate.available && matches(candidate, probe));
      const choice = input.closest('[data-quick-add-choice]');
      choice.classList.toggle('is-unavailable', !inStock);
      const note = choice.querySelector('[data-quick-add-note]');
      if (note) note.hidden = inStock;
    });

    const onSale = Boolean(variant && variant.compare_at_price);
    if (variant) price.textContent = variant.price;
    price.parentElement.classList.toggle('quick-add__price-current--sale', onSale);
    saleNote.hidden = !onSale;
    compare.hidden = !onSale;
    if (onSale) compareText.textContent = variant.compare_at_price;

    const labels = submit.dataset;
    let text = labels.labelAdd;
    if (!variant) text = labels.labelUnavailable;
    else if (!variant.available) text = labels.labelSoldOut;
    submit.disabled = !variant || !variant.available;
    setRollText(submitText, text);
    if (variant) {
      idInput.value = variant.id;
      details.href = variant.url;
    }

    if (!userChange) return;
    showError(''); // what was refused was about the variant chosen before
    if (!variant) say(`${text}.`);
    else say(`${variant.title}: ${variant.available ? variant.price : text}`);
  };

  body.addEventListener('change', (event) => {
    if (event.target.matches('[data-quick-add-option]')) refresh(true);
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
    setBusy(submit, true);
    showError('');
    const fallback = "This couldn't be added to your cart. Please try again.";
    const detail = {
      variantId: idInput.value,
      quantity: 1,
      handled: false,
      done: (ok, message, shown) => {
        setBusy(submit, false);
        if (ok || shown) {
          // The drawer has opened, which closed this window: when the drawer
          // closes, focus goes back to the card's button that started it all.
          const drawer = document.querySelector('[data-cart-drawer]');
          if (drawer && drawer.cartDrawer) drawer.cartDrawer.lastFocused = opener;
          return;
        }
        showError(message || fallback);
      },
    };
    document.dispatchEvent(new CustomEvent('cart:add', { detail }));
    if (detail.handled) return;

    // No drawer on the page: add directly, then show the cart page.
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(idInput.value), quantity: 1 }] }),
      });
      if (response.ok) {
        window.location.href = `${root}cart`;
        return;
      }
      const data = await response.json().catch(() => null);
      showError((data && data.description) || fallback);
    } catch (err) {
      showError(fallback);
    }
    setBusy(submit, false);
  });

  refresh(false);
};

let opening = null;
const openWindow = async (opener) => {
  opening = opener;
  setBusy(opener, true);
  const [page] = await Promise.all([request(opener.href).catch(() => null), stylesReady()]);
  setBusy(opener, false);
  if (opening !== opener) return; // another card's button was pressed meanwhile
  opening = null;
  if (!page) {
    // Nothing to show: go where the link goes, as it would without a script.
    window.location.href = opener.href;
    return;
  }
  const body = document.importNode(page, true);
  borrowCardPhoto(body, opener);
  const { dialog, panel, slot } = getShell();
  slot.replaceChildren(body);
  slot.scrollTop = 0;
  dialog.setAttribute('aria-labelledby', body.dataset.titleId);
  setUpWindow(body, opener);
  // Safari doesn't focus a link when it is clicked: name the way back too.
  panel.returnFocusTo = opener;
  panel.open();
  settlePhoto(body);
};

if (!window.__productQuickAdd) {
  window.__productQuickAdd = true;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-quick-add-open]');
    if (opener) {
      // A click meant for a new tab or window goes where the link goes.
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (opener.getAttribute('aria-disabled') !== 'true') openWindow(opener);
      return;
    }

    const button = event.target.closest('[data-quick-add]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;
    event.preventDefault();
    setBusy(button, true);

    const detail = {
      variantId: button.dataset.variantId,
      quantity: 1,
      handled: false,
      showInCart: true,
      done: (ok, message, shown) => {
        setBusy(button, false);
        if (!ok && !shown) failure(button);
      },
    };
    document.dispatchEvent(new CustomEvent('cart:add', { detail }));
    if (!detail.handled) addWithoutDrawer(button);
  });

  // The window's contents and styles are fetched on intent: hover (or the
  // start of a tap) and keyboard focus.
  document.addEventListener('pointerover', prefetch, { passive: true });
  document.addEventListener('focusin', prefetch);
}
